import React, { useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import {
  type SchedulePickerState,
  parseCronToPicker,
  compilePickerToCron,
  isValidCron,
  DEFAULT_SCHEDULE_PICKER_STATE,
} from '../../utils/cron';
import { TIMEZONE_OPTIONS } from '../../services/routines-api';
import type { OutputChannelConfig } from '../../services/output-channels-api';
import ScheduleField from './ScheduleField';
import OutputChannelsField from './OutputChannelsField';

/**
 * The on-submit shape passed back to the parent. The parent decides what to
 * do (POST for create, PATCH for edit, REST + birthed_from_chat_session_id
 * for proposal-card approval). Cron is already a string here — the picker
 * state lives only inside the form.
 */
export interface RoutineFormSubmit {
  name: string;
  schedule: string;
  timezone: string;
  message: string;
  outputChannels: OutputChannelConfig[];
  enabled: boolean;
}

export interface RoutineFormInitial {
  name?: string;
  /** Cron string — parsed back into picker state on mount. */
  schedule?: string;
  timezone?: string;
  message?: string;
  outputChannels?: OutputChannelConfig[];
  enabled?: boolean;
}

interface Props {
  initial?: RoutineFormInitial;
  organizationTimezone: string;
  onSubmit: (state: RoutineFormSubmit) => Promise<void> | void;
  onCancel?: () => void;
  submitLabel: string;
  showEnabledToggle?: boolean;
  busy?: boolean;
}

const inputBase =
  'px-3 py-2 rounded-xl text-sm bg-white dark:bg-white/5 border border-input dark:border-white/10 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary/40 transition-colors';

export const RoutineForm: React.FC<Props> = ({
  initial,
  organizationTimezone,
  onSubmit,
  onCancel,
  submitLabel,
  showEnabledToggle = false,
  busy = false,
}) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [message, setMessage] = useState(initial?.message ?? '');
  const [timezone, setTimezone] = useState(initial?.timezone ?? organizationTimezone);
  const [outputChannels, setOutputChannels] = useState<OutputChannelConfig[]>(
    initial?.outputChannels ?? [],
  );
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [schedule, setSchedule] = useState<SchedulePickerState>(
    initial?.schedule ? parseCronToPicker(initial.schedule) : DEFAULT_SCHEDULE_PICKER_STATE,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cronString = compilePickerToCron(schedule);
  const cronValid = isValidCron(cronString);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!message.trim()) {
      setError('Prompt is required — this is what the AI will run on each schedule.');
      return;
    }
    if (!cronValid) {
      setError('The schedule is invalid — please pick a valid frequency or fix the cron expression.');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        schedule: cronString,
        timezone,
        message: message.trim(),
        outputChannels,
        enabled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save routine.');
    } finally {
      setSubmitting(false);
    }
  };

  const isBusy = busy || submitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-xl bg-muted border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Routine details
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Weekly revenue check-in"
              className={`${inputBase} w-full`}
              required
            />
          </div>

          {showEnabledToggle && (
            <div className="flex items-center justify-between rounded-lg bg-muted dark:bg-white/[0.03] border border-border px-3 py-2">
              <div>
                <div className="text-sm font-medium text-foreground">Enabled</div>
                <div className="text-xs text-muted-foreground">
                  When off, the routine stays on the list but does not fire on its schedule.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEnabled((v) => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  enabled ? 'bg-primary' : 'bg-muted'
                }`}
                aria-pressed={enabled}
                aria-label="Toggle enabled"
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    enabled ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-muted border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Schedule</h2>
        <ScheduleField value={schedule} onChange={setSchedule} timezone={timezone} />
        <div className="mt-4">
          <label className="block text-sm font-medium text-foreground mb-1">
            Timezone
          </label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className={`${inputBase} w-full md:w-72`}
          >
            {/* Always include the org timezone, even if it's not in the static list */}
            {!TIMEZONE_OPTIONS.some((tz) => tz.value === organizationTimezone) && (
              <option value={organizationTimezone}>
                {organizationTimezone} (organization default)
              </option>
            )}
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-xl bg-muted border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Output channels
        </h2>
        <OutputChannelsField value={outputChannels} onChange={setOutputChannels} />
      </div>

      <div className="rounded-xl bg-muted border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Prompt</h2>
        <p className="text-sm text-muted-foreground mb-3">
          The prompt is sent to the agent on every run. Write it self-contained and time-relative — it must work without knowledge of the conversation that birthed it.
        </p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g., Generate the weekly revenue report for the trailing 7 days, comparing to the prior 7 days. Highlight any anomalies and explain root causes for movements >15%."
          className={`${inputBase} w-full`}
          rows={6}
          required
        />
      </div>

      {error && (
        <div className="bg-destructive/15 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isBusy}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-input dark:border-white/10 text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isBusy || !cronValid}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default RoutineForm;
